// various classes related to RfxCompanyToDo objects

import { DateTime } from 'luxon';
import { companyToDoTypesMapping } from './company_todo_types';

// CLASSES

class CompanyToDoReply {  // a generic version of a Reply (DocRequestReply, QuoteSheetReply, etc)
    constructor({ // set defaults here - make sure to use them because some things are not populated for internal/external use cases
        id,  // required
        category,
        parent_todo_id,  // the 'linked_model' - DocRequest, FormiumForm, etc.
        parent_todo_title,
        company_todo_id,  // CompanyToDo model instance id
        rfx_company_todo_id,  // RfxCompanyToDo instance id - might be empty if not working in context of RfxCompanyToDo
        company_id,  // id of the Company instance
        can_new_submit,
        reply_valid,
        is_submitted,  
        submitted_on,
        created,
        modified,
        error_list,  // list/array of errors
        // add more later... and make sure you're providing all the data for these from API
    }) {
        // set all things from args object here with this.arg = arg;
        this.id = id;
        this.category = category;  // something like 'docrequest', 'formium_form', 'quotesheet'
        this.parent_todo_id = parent_todo_id;
        this.parent_todo_title = parent_todo_title;
        this.company_todo_id = company_todo_id;
        this.rfx_company_todo_id = rfx_company_todo_id;
        this.company_id = company_id;
        this.can_new_submit = can_new_submit;
        this.reply_valid = reply_valid;
        this.is_submitted = is_submitted;
        this.submitted_on = submitted_on ? DateTime.fromISO(submitted_on) : null;
        this.submittedOn = this.submitted_on ? this.submitted_on.toLocaleString() : '';
        this.created = created ? DateTime.fromISO(created) : null;
        this.modified = modified ? DateTime.fromISO(modified) : null;
        this.error_list = error_list ?? [];
    }
    // GETTERS
    // METHODS
}


class RfxCompanyToDo {  // a generic version of a Reply (DocRequestReply, QuoteSheetReply, etc)
    constructor({ // set defaults here - make sure to use them because some things are not populated for internal/external use cases
        id,  // required
        category,
        subcategory,
        created,
        modified,
        position,
        required,
        status,
        title,
        final_instructions,
        instructions,
        rfx_id,
        company_todo_id,
    }) {
        this.id = id;
        this.category = category;
        this.subcategory = subcategory;
        this.category_key = subcategory ? (category + '_' + subcategory) : category;  // used for lookups in list/mapping above
        this.created = created ? DateTime.fromISO(created) : null;
        this.modified = modified ? DateTime.fromISO(modified) : null;
        this.position = position;
        this.required = required;
        this.status = status;  // 'ENABLED' or 'HIDDEN' or 'SUPPLIERLOCKED' for now
        this.title = title;
        this.final_instructions = final_instructions;
        this.instructions = instructions;
        this.rfx_id = rfx_id;
        this.company_todo_id = company_todo_id;
    }
    // GETTERS
    get toDoType() {  // use this in other getters, DRY
        return companyToDoTypesMapping[this.category_key];
    }
    get categoryName() {
        return this.toDoType.title;
    }
    get categoryIcon() {
        return this.toDoType.icon;
    }
    get isHidden() {
        // also return true here if validity is not valid?  we don't know that here...
        return this.status === 'HIDDEN';
    }
    get isSupplierLocked() {
        return this.status === 'SUPPLIERLOCKED';
    }
    // METHODS
}


class RfxCompanyToDoReplyStatus {
    constructor({
        rfx_company_todo,
        company_id,
        selected_reply_is_valid,
        is_valid_without_submit,
        new_submit_required,
        is_submitted,
        has_can_new_submit,
        block_submit,
        current_state,
        last_submitted_reply,
        draft_reply,
        //most_recent_reply,
        selected_reply,
        all_replies,
    }) {
        this.id = rfx_company_todo.id;
        this.rctd = new RfxCompanyToDo(rfx_company_todo);  // add some aliases for it?
        this.company_id = company_id;
        this.is_valid = selected_reply_is_valid;
        this.is_submitted = is_submitted;
        this.is_valid_without_submit = is_valid_without_submit;
        this.new_submit_required = new_submit_required;  // opposite of is_valid_without_submit
        this.has_can_new_submit = has_can_new_submit;
        this.block_submit = block_submit;
        this.current_state = current_state;
        this.lastSubmittedReply = last_submitted_reply ? new CompanyToDoReply({ rfx_company_todo_id: rfx_company_todo.id, ...last_submitted_reply}) : null;
        this.draftReply = draft_reply ? new CompanyToDoReply({ rfx_company_todo_id: rfx_company_todo.id, ...draft_reply}) : null;
        //this.mostRecentReply = most_recent_reply ? new CompanyToDoReply(most_recent_reply) : null;
        this.selectedReply = selected_reply ? new CompanyToDoReply({ rfx_company_todo_id: rfx_company_todo.id, ...selected_reply}) : null;
        this.allReplies = all_replies?.length ? all_replies.map(rep => new CompanyToDoReply({ rfx_company_todo_id: rfx_company_todo.id, ...rep})) : [];
    }
    // GETTERS
    get companyToDo() {
        return this.rctd;  // alias for backwards compatibility
    }
    get category() {
        return this.rctd.category;
    }
    get lastSubmittedReplyId() {
        return this.lastSubmittedReply?.id || '';
    }
    get lastSubmittedReplyValid() {
        return this.lastSubmittedReply?.reply_valid ?? false;
    }
    get draftReplyId() {
        return this.draftReply?.id || '';
    }
    get draftReplyValid() {
        return this.draftReply?.reply_valid ?? false;
    }
    get hasAnyReply() {
        return !!this.draftReply || !!this.lastSubmittedReply || !!this.selectedReply;
    }
    get replyToSubmit() {  // will be null if no reply ready for submit
        return this.draftReply?.can_new_submit ? this.draftReply : null;
    }
    get hasReplyToSubmit() {
        return this.draftReply?.can_new_submit ? true : false;
    }
    get replyStatus() {
        if (!this.lastSubmittedReply && !this.draftReply) return 'Not Started';  // not showing this... because we are currently COMPLETELY hiding draft replies from internal people
        return this.is_valid ? 'Complete' : 'Incomplete';
    }
    get replyStatusBadgeClass() {
        if (!this.lastSubmittedReply && !this.draftReply) return 'soft-secondary';
        return this.is_valid ? 'soft-success' : 'soft-danger';
    }
    get replyState() {
        if (!this.lastSubmittedReply && !this.draftReply) {  // if NO reply at all
            return this.rctd.required ? 'missing' : 'skipped';
        } else if (this.lastSubmittedReply && !this.draftReply) {  // if only last submitted, no draft
            return this.lastSubmittedReply.reply_valid ? 'submitted_valid' : 'submitted_invalid';
        } else {  // has a draft  (may also have submitted, but that's less important if we have a draft)
            return this.draftReply.reply_valid ? 'draft_valid' : 'draft_invalid';
        }
    }
    get replyStatusInternalHeading() {
        switch (this.current_state) {
            case 'submitted_invalid':
                return 'Submitted'
            case 'draft_invalid':
                return 'In Process';
            case 'missing':
                return 'Not Completed';
            case 'skipped':
                return 'Not Completed';
            case 'submitted_valid':
                return 'Submitted';
            case 'draft_valid':
                return 'In Process';
            default:
                return '';
        }
    }
    get replyStatusSubmitHeading() {  // the phrase we show on the submit confirm component (for EXTERNAL use)
        switch (this.replyState) {
            case 'submitted_invalid':
            case 'draft_invalid':
                return 'Needs Attention';
            case 'missing':
                return 'Incomplete';
            case 'skipped':
                return 'Not Completed';
            case 'submitted_valid':
                return 'Submitted';
            case 'draft_valid':
                return 'Ready to Submit';
            default:
                return '';
        }
    }
    get replyStatusSubmitSubheading() {
        switch (this.replyState) {
            case 'submitted_invalid':
                return 'Please check previous submission'
            case 'draft_invalid':
                return 'Must fix before submitting';
            case 'missing':
                return 'This is required';
            case 'skipped':
                return '';
            case 'submitted_valid':
                return 'on ' + this.lastSubmittedReply.submittedOn;
            case 'draft_valid':
                return '';
            default:
                return '';
        }
    }
    get replyStatusSubmitIcon() {
        switch (this.replyState) {
            case 'submitted_invalid':
            case 'draft_invalid':
            case 'missing':
                return 'x'
            case 'skipped':
                return '';
            case 'submitted_valid':
            case 'draft_valid':
                return 'check';
            default:
                return '';
        }
    }
    get replyStatusSubmitVariant() {
        switch (this.replyState) {
            case 'submitted_invalid':
            case 'draft_invalid':
            case 'missing':
                return 'danger'
            case 'skipped':
                return 'secondary';
            case 'submitted_valid':
            case 'draft_valid':
                return 'success';
            default:
                return 'secondary';
        }
    }
    // METHODS
    getReplyById(replyId) {
        console.log(`From getReplyById - replyId is ${replyId}`);
        // run through all the replies currently in this object, find one matching replyId, or null if not found
        // check all the ones that have validation info with them first
        if (!replyId) return null;
        if (replyId === this.lastSubmittedReply?.id) return this.lastSubmittedReply;
        if (replyId === this.draftReply?.id) return this.draftReply;
        // not checking any others... must be one of these
        return null;
    }
    checkReplyValid(replyId) {
        // must pass in a replyId, this will tell you if that replyId is currently valid based on locally contained info
        // use case:  if you're letting user select which draft replies they're submitting (which version), pass in the reply selected by user to this
        // RETURN:  Boolean, or null.  null = we don't know or reply not found
        console.log(`From checkReplyValid - replyId is ${replyId}`);
        if (!replyId) return null;
        let theReply = this.getReplyById(replyId);
        console.log(`From checkReplyValid - theReply is below`);
        console.log(theReply);
        return theReply ? theReply.reply_valid : null;
    }
    allowSubmit(replyId) {
        // can we submit based on state of this todo?  If submitting a NEW reply for this todo, pass in the replyId
        // if you don't pass in a replyId, it will use boolean this.is_valid_without_submit, which our backend determines (based on if its optional, and on validity of any previous reply)
        // this is what you'll run through before submission to make sure a bundle of todos can be submitted
        // if RfxCompanyToDo is OPTIONAL, we will still enforce validity -- why?  if buyer wants to make things optional, then make items optional on the form
        // ... therefore, we won't allow any replies to be submitted that return false on their own validation checks, whether that todo is required or optional
        return replyId ? this.checkReplyValid(replyId) : this.is_valid_without_submit;
    }
}


export {
    CompanyToDoReply,
    RfxCompanyToDo,
    RfxCompanyToDoReplyStatus,
}